import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import useDebounce from "../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { useGetBalanceHistoriesQuery } from "../../services/finance.service";
import { BalanceHistory, ManualDeposit } from "../../types/finance";
import { Talent } from "../../types/users";
import dayjs from "dayjs";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { formatMoney } from "../../utils/helper";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";

const TalentFundsPanel = ({ data: dataTalent }: { data?: Talent; }) => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const { data, isLoading, isFetching } = useGetBalanceHistoriesQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
    modelType: "user",
    model: dataTalent?.id,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        minWidth: isMobile ? 140 : 200,
        flex: isMobile ? 0 : undefined,
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: false,
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${dayjs(params.row?.created_at).format("MMM DD, YYYY hh:mm A")}`,
      },
      {
        field: "notes",
        headerName: "Notes",
        minWidth: isMobile ? 200 : 400,
        flex: isMobile ? 0 : 1,
        sortingOrder: ["desc", "asc", null],
        sortable: false,
        filterable: false,
      },
      {
        field: "credit",
        headerName: "Credit",
        minWidth: isMobile ? 100 : 200,
        flex: isMobile ? 0 : undefined,
        type: "number",
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.credit)}`,
      },
      {
        field: "debit",
        headerName: "Debit",
        minWidth: isMobile ? 100 : 200,
        flex: isMobile ? 0 : undefined,
        type: "number",
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "center",
        valueGetter: (
          params: GridValueGetterParams<undefined, BalanceHistory>
        ) => `${formatMoney(params.row.debit)}`,
      },
      // {
      //   field: "balance",
      //   headerName: "Balance",
      //   flex: 1,
      //   type: "number",
      //   sortable: true,
      //   filterable: true,
      // },
    ],
    [isMobile]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <CardBase
      title="Finances"
      useGlassCard={false}
      sx={{ height: "85vh", p: 0 }}
      headerActions={
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <StyledTextField
            size="small"
            label="Enter to search.."
            value={query || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            sx={{
              minWidth: { xs: "100%", sm: 250 },
              width: { xs: "100%", sm: "auto" },
            }}
          />
          {!!query && (
            <Button
              variant="text"
              onClick={() => setQuery("")}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "auto" },
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                },
              }}
            >
              Reset
            </Button>
          )}
        </Stack>
      }
    >
      <GlassDataGrid
        rows={rows || []}
        columns={columns}
        paginationMode="server"
        rowCount={rowCount}
        page={page}
        onPageChange={(newPage: number) => setPage(newPage)}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageSizeChange={(newSize: number) => setPageSize(newSize)}
        sortModel={sortModel}
        sortingMode="server"
        onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
        loading={isLoading || isFetching}
        components={{
          LoadingOverlay: OrangeLinearProgress,
          NoResultsOverlay: NoResultsOverlay,
        }}
        disableSelectionOnClick
        height={isMobile ? "calc(85vh - 140px)" : "calc(85vh - 80px)"}
        sx={{
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "transparent",
            borderTop: "none",
            paddingLeft: { xs: 1, sm: 3 },
            paddingRight: { xs: 1, sm: 3 },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#F4A259",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
            },
            "& .MuiDataGrid-columnHeader": {
              color: "#F4A259",
              "& .MuiDataGrid-iconButtonContainer": {
                color: "#F4A259",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "#F4A259",
              },
            },
          },
          "& .MuiDataGrid-cell": {
            paddingLeft: { xs: 1, sm: 3 },
            paddingRight: { xs: 1, sm: 3 },
            py: { xs: 1, sm: 2 },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "rgba(244, 162, 89, 0.05)",
          },
          "& .MuiDataGrid-main": {
            padding: { xs: 1, sm: 2 },
          },
          "& .MuiDataGrid-footerContainer": {
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          },
          "& .deleted": {
            fontStyle: "italic",
            color: "#aaa",
          },
        }}
        getRowClassName={getRowClassName}
      />
    </CardBase>
  );
};
export default TalentFundsPanel;
